import React, { useEffect, useRef, useState } from "react";
import { Dropdown, Menu, Pagination, Popconfirm, Spin } from "antd";
import {
  MoreOutlined,
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useGetChatHistory } from "./hooks/useGetChatHistory";
import { Item } from "../../../types";
import JobDescriptionModal from "features/cvChat/components/job-description-modal";
import ItemDetailsPage from "features/cvChat/components/item-details-page";

const DocumentHistoryPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "All" | "Pending" | "Completed"
  >("All");
  const [isJobDescriptionModalVisible, setIsJobDescriptionModalVisible] =
    useState(false);
  const [modalJDFile, setModalJDFile] = useState<File | null>(null);
  const [activeItemId, setActiveItemId] = useState<number | null>(null);
  const [showDetailsPage, setShowDetailsPage] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const cvInputRef = useRef<HTMLInputElement | null>(null);

  const [page, setPage] = useState(1);
  const pageSize = 15;
  const { data, isLoading, isError, refetch } = useGetChatHistory({
    page,
    perPage: pageSize,
  });
  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const handleViewClick = (item: any, e: React.SyntheticEvent) => {
    e.stopPropagation();
    setSelectedItem(item);
    setShowDetailsPage(true);
  };

  const handleCloseDetailsPage = () => {
    setShowDetailsPage(false);
    setSelectedItem(null);
  };

  const handleAddClick = (item: any, e: React.SyntheticEvent) => {
    e.stopPropagation();
    setActiveItemId(item.id);
    cvInputRef.current?.click();
  };

  const handleJDNameClick = (item: any, e: React.SyntheticEvent) => {
    e.stopPropagation();
    setSelectedItem(item);
    setIsJobDescriptionModalVisible(true);
    setModalJDFile(item.jdFile || null);
  };

  const handleDeleteClick = (id: number) => {
    console.log("Deleting batch with id:", id);
  };

  const filteredItems = items.filter((item: Item) => {
    const matchesSearch = item.title
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "All" ? true : item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const [analysisResults, setAnalysisResults] = useState<{
    [key: string | number]: any[];
  }>({});

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter]);

  // Show details page when an item is selected
  if (showDetailsPage && selectedItem) {
    return (
      <ItemDetailsPage
        item={selectedItem}
        analysisResults={analysisResults}
        onBack={handleCloseDetailsPage}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spin size="large" tip="Loading document history..." />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center text-red-500 mt-20">
        Failed to load document history. Please try again.
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full p-6 bg-gray-50 flex-col">
      <div className="flex gap-4 mb-4 w-full">
        {/* Search by Name */}
        <div className="flex-1 flex flex-col">
          <label className="text-md font-medium text-gray-700 mb-2">
            Name of Document
          </label>
          <input
            type="text"
            placeholder="Enter name..."
            className="border rounded px-3 py-2 w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Filter by Status */}
        <div className="flex-1 flex flex-col">
          <label className="text-md font-medium text-gray-700 mb-2">
            Filter by Status
          </label>
          <select
            className="border rounded px-3 py-2 w-full"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
          >
            <option value="All">All</option>
            <option value="Pending">Pending</option>
            <option value="Completed">Completed</option>
          </select>
        </div>
      </div>

      {items.length > 0 ? (
        <div className="border rounded-lg bg-white p-6 shadow-sm overflow-x-auto w-full">
          <table className="w-full text-sm text-left border-collapse mb-4">
            <thead>
              <tr className="border-b text-gray-700 font-semibold">
                <th className="py-2 px-3">Name</th>
                <th className="py-2 px-3">No of Profiles</th>
                <th className="py-2 px-3">Date</th>
                <th className="py-2 px-3">Time</th>
                <th className="py-2 px-3">Status</th>
                <th className="py-2 px-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item: Item) => (
                <tr
                  key={item.id}
                  className="border-b hover:bg-gray-50 cursor-pointer"
                >
                  <td className="py-2 px-3">{item.title || "--"}</td>
                  <td className="py-2 px-3">
                    {item.profiles_count ?? (item.profiles ?? []).length}
                  </td>
                  <td className="py-2 px-3">
                    {item.created_at
                      ? new Date(item.created_at).toLocaleDateString()
                      : "--"}
                  </td>
                  <td className="py-2 px-3">
                    {item.created_at
                      ? new Date(item.created_at).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "--"}
                  </td>
                  <td
                    className={`py-2 px-3 font-medium ${
                      item.status === "Completed"
                        ? "text-green-600"
                        : "text-green-600"
                    }`}
                  >
                    {item.status || "Completed"}
                  </td>
                  <td
                    className="py-2 px-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Dropdown
                      trigger={["click"]}
                      overlay={
                        <Menu>
                          <Menu.Item
                            key="view"
                            icon={<EyeOutlined className="text-green-500" />}
                            onClick={(e) => handleViewClick(item, e.domEvent)}
                          >
                            <span className="text-green-600 font-medium">
                              View
                            </span>
                          </Menu.Item>
                          <Menu.Item
                            key="add"
                            icon={<PlusOutlined className="text-blue-500" />}
                            onClick={(e) => handleAddClick(item, e.domEvent)}
                          >
                            <span className="text-blue-600 font-medium">
                              Add
                            </span>
                          </Menu.Item>
                          <Menu.Item
                            key="delete"
                            icon={<DeleteOutlined className="text-red-500" />}
                          >
                            <Popconfirm
                              title="Delete Batch"
                              description="Do you want to permanently delete this batch?"
                              okText="Yes, delete"
                              cancelText="Cancel"
                              okButtonProps={{ danger: true, type: "primary" }}
                              onConfirm={(e) => {
                                e?.stopPropagation();
                                handleDeleteClick(Number(item.id));
                              }}
                              onCancel={(e) => e?.stopPropagation()}
                            >
                              <span className="text-red-600 font-medium">
                                Delete
                              </span>
                            </Popconfirm>
                          </Menu.Item>
                        </Menu>
                      }
                    >
                      <MoreOutlined className="text-xl text-gray-600 cursor-pointer hover:text-blue-600" />
                    </Dropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-end">
            <Pagination
              current={page}
              pageSize={pageSize}
              total={total}
              onChange={(p) => setPage(p)}
              showSizeChanger={false}
            />
          </div>
        </div>
      ) : (
        <p className="text-gray-500 text-center mt-20">
          No document batches uploaded yet.
        </p>
      )}

      <JobDescriptionModal
        jdFile={modalJDFile}
        open={isJobDescriptionModalVisible}
        onClose={() => setIsJobDescriptionModalVisible(false)}
      />
    </div>
  );
};

export default DocumentHistoryPage;
